import { Box, Stack, type StackProps, Typography, styled } from "@mui/material"
import {
  HealthMissionsGoalsEnum,
  assertNever,
  pluralize,
  useAchievementColor,
} from "@pharmupp/p3-front-commons"
import type { PropsWithChildren, ReactNode } from "react"
import { AlertCircle, AlertTriangle, Check, type Icon } from "react-feather"
import type { GoalProgressState } from "../getGoalProgress"

type ProgressMessageProps = {
  goalProgress: GoalProgressState
  missionCount: number
} & Omit<StackProps, "color">

type ProgressDisplayArgs = Pick<
  GoalProgressState,
  "lastReachedGoal" | "nextGoal"
> & {
  missionCount: number
}

export const ProgressMessage = ({
  goalProgress: { isAchieved, lastReachedGoal, nextGoal },
  missionCount,
  ...stackProps
}: ProgressMessageProps) => {
  const { bgColor, color } = useAchievementColor(isAchieved)
  const { Icon, Message } = getProgressDisplay({
    missionCount,
    lastReachedGoal,
    nextGoal,
  })

  return (
    <ProgressMessageContainer bgColor={bgColor} color={color} {...stackProps}>
      <IconContainer isChecked={isAchieved}>
        <Icon size={14} />
      </IconContainer>
      {Message}
    </ProgressMessageContainer>
  )
}

export const getProgressDisplay = ({
  missionCount,
  lastReachedGoal,
  nextGoal,
}: ProgressDisplayArgs): {
  Message: ReactNode
  Icon: Icon
} => {
  if (missionCount === 0 && nextGoal) {
    return {
      Message: <NoActionMessage />,
      Icon: AlertCircle,
    }
  }

  if (missionCount > 0) {
    if (nextGoal && !lastReachedGoal) {
      return {
        Message: REMAINING_TO_STEP_MESSAGES[nextGoal.type](
          nextGoal.value - missionCount,
        ),
        Icon: AlertCircle,
      }
    }

    if (lastReachedGoal) {
      return {
        Message: REACHED_STEP_MESSAGES[lastReachedGoal.type],
        Icon: Check,
      }
    }
  }

  return assertNever(
    nextGoal as never,
    {
      Message: "Une erreur est survenue",
      Icon: AlertTriangle,
    },
    "nextGoal -> getProgressDisplay, ProgressMessage.tsx",
  )
}

// MESSAGES

const NoActionMessage = () => (
  <Txt>
    <Bold>Lancez-vous !</Bold> Chaque mission de santé renforce le rôle de votre
    pharmacie comme <BoldLight>acteur de santé de proximité</BoldLight>.
  </Txt>
)

const RemainingMissions = ({
  remainingMissions,
  children,
}: PropsWithChildren<{ remainingMissions: number }>) => (
  <Txt>
    Plus que{" "}
    <BoldLight>
      {remainingMissions} {pluralize(remainingMissions, "acte", "actes")}{" "}
    </BoldLight>
    {children}
  </Txt>
)

const REMAINING_TO_STEP_MESSAGES: Record<
  HealthMissionsGoalsEnum,
  (remainingMissions: number) => ReactNode
> = {
  [HealthMissionsGoalsEnum.FIRST_QUARTILE]: (remainingMissions) => (
    <RemainingMissions remainingMissions={remainingMissions}>
      pour intégrer le 1er quartile des pharmaciens UPP sur cet acte de santé.
    </RemainingMissions>
  ),
  [HealthMissionsGoalsEnum.EXPLOITED_POTENTIAL_75]: (remainingMissions) => (
    <RemainingMissions remainingMissions={remainingMissions}>
      pour atteindre <Bold>75% de votre potentiel</Bold> sur cet acte de santé.
    </RemainingMissions>
  ),
}

const FirstQuartileReachedMessage = () => (
  <Txt success>
    <Bold>Bravo</Bold>, vous vous situez dans le{" "}
    <BoldSuccess>1er quartile</BoldSuccess> des pharmaciens UPP sur cet acte de
    santé.
  </Txt>
)

const ExploitedPotential75ReachedMessage = () => (
  <Txt success>
    <Bold>Félicitations</Bold>, vous avez déjà optimisé{" "}
    <BoldSuccess>75% de votre potentiel</BoldSuccess> sur cet acte
  </Txt>
)

const REACHED_STEP_MESSAGES: Record<HealthMissionsGoalsEnum, ReactNode> = {
  [HealthMissionsGoalsEnum.FIRST_QUARTILE]: <FirstQuartileReachedMessage />,
  [HealthMissionsGoalsEnum.EXPLOITED_POTENTIAL_75]: (
    <ExploitedPotential75ReachedMessage />
  ),
}

// STYLED
const Txt = styled(Typography, {
  shouldForwardProp: (props) => props !== "success",
})<{ success?: boolean }>(({ theme: { palette }, success }) => ({
  fontWeight: 500,
  color: success ? palette.success.dark : palette.common.darkBlue,
}))
Txt.defaultProps = {
  variant: "12px",
}

const Bold = styled(Txt)({
  fontWeight: 700,
  color: "inherit",
})
const BoldSuccess = styled(Bold)(({ theme }) => ({
  color: theme.palette.success.main,
}))
const BoldLight = styled(Bold)(({ theme }) => ({
  color: theme.palette.branding.light3,
}))

const tint = (color: string, pct: number) =>
  `color-mix(in srgb, ${color} ${pct}%, #fff)`

const ProgressMessageContainer = styled(Stack, {
  shouldForwardProp: (props) => !["bgColor", "color"].includes(String(props)),
})<{ bgColor: string; color: string }>(({ theme, bgColor, color }) => ({
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing(2.15),
  borderRadius: theme.spacing(1),
  border: `1px solid ${tint(color, 25)}`,
  backgroundColor: bgColor,
  padding: theme.spacing(1.75),
}))

const IconContainer = styled(Box, {
  shouldForwardProp: (props) => props !== "isChecked",
})<{ isChecked: boolean }>(({ theme: { palette, spacing }, isChecked }) => ({
  display: "grid",
  placeContent: "center",
  backgroundColor: isChecked ? palette.success.main : palette.branding.light3,
  color: palette.common.white,
  padding: spacing(1),
  borderRadius: "100px",
  height: "30px",
  aspectRatio: "1/1",
}))
